import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import { useUser } from "contexts/UserContext";
import { useSubscription } from "contexts/subscription-context";
import messages from "messages";
import { useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import MinorInfoEditor from "./components/minor-info-editor/MinorInfoEditor";
import PatientInfoEditor from "./components/patient-info-editor/PatientInfoEditor";
import { PATIENT_TYPES } from "./constants/patientTypes";
import { BILLING_TYPES } from "./constants/billingTypes";
import { MINOR_INFO_TABS } from "./constants/minorInfoTabs";
// components
import DatePicker from "components/date-picker/DatePicker";
import FormControlTime from "components/form-control-time/FormControlTime";
import { track } from "utils/amplitude";

// styles
import getClasses from "utils/getClasses";
import styles from "./PatientEditor.module.scss";

const ANALYTICS_COMPONENT_NAME = "patient-editor-modal";

const BILLING_CONTACT_RADIOS = [
  { value: "patient1", label: "Partner 1" },
  { value: "patient2", label: "Partner 2" },
];

const PatientEditor = ({
  type,
  setType,
  values,
  setValues,
  billingType,
  setBillingType,
  billingContact,
  setBillingContact,
  setGuardianType,
  coupleOrContactUser,
  addCoupleOrContactAsPatient,
  amplitudePageName,
  waitlist,
  setWaitlist,
  waitlistDateAdded,
  setWaitlistDateAdded,
  setReasonForVisit,
  setRequestedTimes,
  reasonForVisit,
  requestedTimes,
  handleTimeChange,
  handleAddTime,
}) => {
  const { user, defaultUserTimezone } = useUser();
  const { canAccessFeature } = useSubscription();

  // only for minor
  const [key, setKey] = useState(MINOR_INFO_TABS.patient.id);

  return (
    <div className={styles["patient-editor__form-content"]}>
      {/* type selector */}
      <div className={styles["patient-editor__section"]}>
        <div
          className={styles["patient-editor__radio-group"]}
          role="radiogroup"
          aria-label="Patient Type"
        >
          {Object.values(PATIENT_TYPES).map((el) => (
            <div
              className={styles["patient-editor__radio-option"]}
              key={el.value}
            >
              <CustomSelectionControl
                type="radio"
                checked={type === el.value}
                onChange={() => {
                  if (el.value !== type) {
                    setType(el.value);
                    track(
                      amplitudePageName,
                      [
                        ANALYTICS_COMPONENT_NAME,
                        `${el.value}-patient-type-radio-button`,
                      ],
                      "click",
                    );
                  }
                }}
                disabled={
                  (!!coupleOrContactUser && el.value !== "couple") ||
                  addCoupleOrContactAsPatient
                }
              />{" "}
              {messages[el.stringId]}
            </div>
          ))}
        </div>
      </div>

      {/* adult */}
      {/* information for different types of patient */}
      {type === PATIENT_TYPES["adult"].value && (
        <PatientInfoEditor
          value={values[0]}
          onChange={(arg) => setValues([arg])}
          className={styles["patient-editor__section"]}
          labelClass={styles["patient-editor__section-header"]}
          disableSelectFromExisting={true}
          amplitudePageName={amplitudePageName}
          amplitudeComponentName={ANALYTICS_COMPONENT_NAME}
        />
      )}
      {/* minor */}
      {/* information for different types of patient */}
      {type === PATIENT_TYPES["minor"].value && (
        <MinorInfoEditor
          values={values}
          onChange={(arg) => setValues(arg)}
          className={styles["patient-editor__section"]}
          labelClass={styles["patient-editor__section-header"]}
          onBillingContactChange={(patient) => setBillingContact(patient)}
          billingContact={billingContact}
          billingType={billingType}
          setBillingType={setBillingType}
          onGuardianTypeChange={(guardianType) => setGuardianType(guardianType)}
          type={type}
          amplitudePageName={amplitudePageName}
          amplitudeComponentName={ANALYTICS_COMPONENT_NAME}
          activeKey={key}
          setKey={setKey}
        />
      )}
      {/* couple */}
      {/* information for different types of patient */}
      {type === PATIENT_TYPES["couple"].value && (
        <>
          {Array.from({ length: 2 }, (_, i) => (
            <PatientInfoEditor
              key={i}
              value={values[i]}
              onChange={(arg) =>
                setValues([...values.slice(0, i), arg, ...values.slice(i + 1)])
              }
              label={[messages.partner, i + 1].join(" ")}
              className={styles["patient-editor__section"]}
              labelClass={styles["patient-editor__section-header"]}
              disableSelectFromExisting={i === 0}
              coupleOrContactUser={i === 0 ? coupleOrContactUser : null}
              amplitudePageName={amplitudePageName}
              amplitudeComponentName={ANALYTICS_COMPONENT_NAME}
            />
          ))}
          {/* billing contact */}
          <div className={styles["patient-editor__section"]}>
            <div
              className={styles["patient-editor__section-header"]}
              id={"Billing Contact"}
            >
              Billing Contact (Payer)
            </div>

            <div
              className={styles["patient-editor__radio-group"]}
              role="radiogroup"
              aria-labelledby={"Billing Contact"}
            >
              {BILLING_CONTACT_RADIOS.map((el) => (
                <div
                  className={styles["patient-editor__radio-option"]}
                  key={el.value}
                >
                  <CustomSelectionControl
                    type="radio"
                    checked={billingContact === el.value}
                    onChange={() => {
                      if (el.value !== billingContact) {
                        setBillingContact(el.value);
                        track(
                          amplitudePageName,
                          [
                            ANALYTICS_COMPONENT_NAME,
                            `billing-contact-${el.value}-radio-button`,
                          ],
                          "click",
                        );
                      }
                    }}
                  />{" "}
                  {el.label}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* billing type */}
      {type === PATIENT_TYPES["minor"].value ? null : (
        <div className={styles["patient-editor__section"]}>
          <div
            className={styles["patient-editor__section-header"]}
            id={"Billing Type"}
          >
            {messages.billingType}
          </div>

          <div
            className={styles["patient-editor__radio-group"]}
            role="radiogroup"
            aria-labelledby={messages.billingType}
          >
            {BILLING_TYPES.map((el) => (
              <div
                className={styles["patient-editor__radio-option"]}
                key={el.value}
              >
                <CustomSelectionControl
                  type="radio"
                  checked={billingType === el.value}
                  onChange={() => {
                    if (el.value !== billingType) {
                      setBillingType(el.value);
                      track(
                        amplitudePageName,
                        [ANALYTICS_COMPONENT_NAME, `${el.value}-radio-button`],
                        "click",
                      );
                    }
                  }}
                />{" "}
                {el.label}
              </div>
            ))}
          </div>
        </div>
      )}
      {key !== MINOR_INFO_TABS.contact.id && (
        <div
          className={
            !canAccessFeature("waitlist") ? "d-none" : styles["waitlist"]
          }
        >
          <hr />
          <div className={styles["patient-editor__radio-option"]}>
            <CustomSelectionControl
              type={"checkbox"}
              checked={waitlist}
              onChange={() => {
                if (waitlist) {
                  setWaitlist(false);
                  setWaitlistDateAdded(new Date().toISOString());
                  setReasonForVisit("");
                  setRequestedTimes([
                    {
                      date: new Date().toISOString(),
                      time: "09:00",
                    },
                  ]);
                } else {
                  setWaitlist(true);
                }
              }}
            />{" "}
            Add to Waitlist
          </div>
          {waitlist && (
            <>
              <div className={styles["waitlist__section-header"]}>
                Waitlist Details
              </div>
              <Form.Group>
                <Form.Label className={styles["waitlist__form-label"]}>
                  Date Added to Waitlist
                </Form.Label>
                <DatePicker
                  wrapperClassName={styles["waitlist__datepicker"]}
                  returnStartOfDay
                  timezone={defaultUserTimezone}
                  dateFormat={"MM/dd/yyyy"}
                  selected={new Date(waitlistDateAdded || 0)}
                  onChange={(date: Date) => {
                    track(
                      amplitudePageName,
                      [ANALYTICS_COMPONENT_NAME, "waitlist-date-picker"],
                      "click",
                    );
                    setWaitlistDateAdded(date.toISOString());
                  }}
                />
              </Form.Group>

              <Form.Group controlId="requestedClinician">
                <Form.Label className={styles["waitlist__form-label"]}>
                  Requested Clinician
                </Form.Label>

                <Form.Control
                  type="text"
                  value={user?.first_name + " " + user?.last_name + " (You)"}
                  disabled
                />
              </Form.Group>

              <Form.Group controlId="reasonForVisit">
                <Form.Label className={styles["waitlist__form-label"]}>
                  Reason for Visit
                </Form.Label>

                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter Reason for Visit"
                  value={reasonForVisit}
                  onChange={(e) => setReasonForVisit(e.target.value)}
                />
              </Form.Group>

              <div
                className={getClasses(
                  styles["patient-editor__section-header"],
                  "mb-2",
                )}
              >
                Requested Times
              </div>
              {requestedTimes.map((el, index) => (
                <Row key={index} className="mb-2">
                  <Col sm={6} md={6} className={"pl-0 pr-1"}>
                    <DatePicker
                      wrapperClassName={styles["waitlist__datepicker"]}
                      returnStartOfDay
                      timezone={defaultUserTimezone}
                      dateFormat={"MM/dd/yyyy"}
                      selected={new Date(el.date || 0)}
                      onChange={(date: Date) => {
                        track(
                          amplitudePageName,
                          [
                            ANALYTICS_COMPONENT_NAME,
                            "waitlist-requested-date-picker",
                          ],
                          "click",
                        );
                        handleTimeChange(index, "date", date.toISOString());
                      }}
                    />
                  </Col>
                  <Col sm={6} md={6} className={"pr-0 pl-1"}>
                    <FormControlTime
                      className={styles["waitlist__time"]}
                      value={el.time ?? ""}
                      onChange={(e) => {
                        track(
                          amplitudePageName,
                          [
                            ANALYTICS_COMPONENT_NAME,
                            "waitlist-requested-time-picker",
                          ],
                          "click",
                        );
                        handleTimeChange(index, "time", e.target.value);
                      }}
                    />
                  </Col>
                </Row>
              ))}
              <Button className={"p-0"} variant="link" onClick={handleAddTime}>
                + Add Times
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PatientEditor;
